import React, { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";

import { unitMeasurementKgOrLbs } from "pages/shipper/RequestForm/components/PackingList/PackingListDefaultOptions";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import FormField from "components/molecules/FormField/FormField";

interface WeightFieldProps {
  isRequired: boolean;
  defaultWUnit?: string;
}

function WeightField({ isRequired, defaultWUnit }: WeightFieldProps) {
  const intl = useIntl();
  const { getValues, control } = useFormContext();
  const [weight, units, wUnit] = useWatch({
    name: ["weight", "units", "w_unit"],
    control,
  });

  // Calculate the total weight of the item
  const totalWeight = useMemo(() => {
    const calculatedWeight = wUnit === "total_kg" || wUnit === "total_lbs" ? weight : weight * units;
    return isNaN(calculatedWeight) ? 0 : calculatedWeight;
  }, [weight, units, wUnit]);

  const wUnitDefault = getValues("w_unit") || defaultWUnit || unitMeasurementKgOrLbs(intl)[0].value;

  return (
    <>
      <FormField
        label={
          getValues(`w_unit`) === "total_lbs" || getValues(`w_unit`) === "total_kg"
            ? "Total weight"
            : intl.formatMessage({
                id: "new-request.packing-list.pieceWeight",
                defaultMessage: "Piece weight",
              })
        }
        type="textinput"
        placeholder={intl.formatMessage({
          id: "new-request.packing-list.pieceWeight",
          defaultMessage: "Piece weight",
        })}
        field_id="weight"
        validations={{
          validate: {
            onlyNumber: (v: any) => !isNaN(v) || "Please enter a valid number",
          },
          setValueAs: (v: string) => (!v ? null : v),
          shouldUnregister: true,
        }}
        mandatory={isRequired}
        containerClasses="w-90-px"
      />
      <div className="w-90-px pt-1">
        <Dropdown
          useHookForm
          name="w_unit"
          className="mt-4 ml-2"
          options={unitMeasurementKgOrLbs(intl)}
          defaultValue={wUnitDefault}
        />
      </div>
      <div className="w-90-px ml-3">
        <p className="font-b3 text-secondary">
          <FormattedMessage id="quote-request-detail.totalWeight" defaultMessage="Total weight" />
        </p>
        <p className="font-b4 mt-3">
          {totalWeight}{" "}
          {getValues("w_unit") === "total_kg" || getValues("w_unit") === "total_lbs"
            ? getValues("w_unit").split("_")[1]
            : getValues("w_unit")}
        </p>
      </div>
    </>
  );
}

export default WeightField;
